import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageContainer } from '../../../common/components/PageContainer';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import { NotificationCategory, useNotificationsApi } from '../../../common/hooks/use-notifications-api.hook';
import { NotificationsInbox } from '../../../modules/notifications/NotificationsInbox';
import { NotificationSettings } from '../../../modules/notifications/NotificationSettings';

const TABS = ['inbox', 'settings'] as const;
type Tab = (typeof TABS)[number];

/**
 * Notifications, in full — for vendors under Home, for staff at /notifications.
 * The bell shows the latest; this is where the rest lives and where each
 * person chooses what reaches them and how.
 */
const NotificationsPage: React.FC<{ portal: 'vendor' | 'staff' }> = ({ portal }) => {
  const { t } = useTranslation();
  const api = useNotificationsApi();
  const [tab, setTab] = useTabParam<Tab>(TABS);
  const [categories, setCategories] = useState<NotificationCategory[]>([]);

  useEffect(() => {
    api.getPreferences().then((p) => setCategories(p.categories)).catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const crumbs = portal === 'vendor'
    ? [{ label: t('AGENTS.NAV.HOME', 'Home'), href: '/home/dashboard' }, { label: t('ARDI.NOTIFICATIONS.TITLE') }]
    : [{ label: t('ARDI.NOTIFICATIONS.TITLE') }];

  return (
    <PageContainer breadcrumbs={crumbs}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('ARDI.NOTIFICATIONS.TITLE')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {t(portal === 'vendor' ? 'ARDI.NOTIFICATIONS.SUBTITLE' : 'ARDI.NOTIFICATIONS.SUBTITLE_STAFF')}
            </p>
          </div>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList className="h-9">
              <TabsTrigger value="inbox" className="px-3 text-sm">{t('ARDI.NOTIFICATIONS.INBOX_TAB')}</TabsTrigger>
              <TabsTrigger value="settings" className="px-3 text-sm">{t('ARDI.NOTIFICATIONS.SETTINGS_TAB')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {tab === 'inbox' ? <NotificationsInbox categories={categories} /> : <NotificationSettings withChannels={portal === 'vendor'} />}
      </div>
    </PageContainer>
  );
};

export default NotificationsPage;
